import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiStartQuoteViccLookup,
  apiGetQuoteViccLookup,
  type ViccLookupResult,
} from 'api/quote-marketplace';

const VICC_POLL_INTERVAL = 1000;

export type ViccLookupStatus =
  | 'idle'
  | 'loading'
  | 'polling'
  | 'found'
  | 'error'
  | 'not_found';

interface UseViccLookupOptions {
  quoteId: number;
  vehicleId: number;
  onResult?: (result: ViccLookupResult) => void;
}

interface UseViccLookupReturn {
  result: ViccLookupResult | null;
  status: ViccLookupStatus;
  isLoading: boolean;
  isPolling: boolean;
  triggerLookup: (viccCode: string) => Promise<ViccLookupResult | null>;
  clear: () => void;
}

const isTerminalStatus = (result: ViccLookupResult | undefined): boolean => {
  if (!result) return false;
  if (result.found) return true;
  if (result.status === 'found') return true;
  if (result.status === 'error') return true;
  if (result.status === 'not_found') return true;
  return false;
};

export const useViccLookup = ({
  quoteId,
  vehicleId,
  onResult,
}: UseViccLookupOptions): UseViccLookupReturn => {
  const queryClient = useQueryClient();
  const queryKey = ['viccLookup', quoteId, vehicleId];
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const [pendingVicc, setPendingVicc] = useState<string | null>(null);

  const { data: pollResult } = useQuery({
    queryKey: [...queryKey, pendingVicc],
    queryFn: () => apiGetQuoteViccLookup(quoteId, pendingVicc!),
    enabled: !!pendingVicc,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && isTerminalStatus(data)) {
        return false;
      }
      return VICC_POLL_INTERVAL;
    },
    staleTime: 0,
    gcTime: 1000 * 60 * 30,
  });

  useEffect(() => {
    if (pollResult && isTerminalStatus(pollResult)) {
      setPendingVicc(null);
      queryClient.setQueryData([...queryKey, pollResult.vicc_code], pollResult);
      onResultRef.current?.(pollResult);
    }
  }, [pollResult, queryClient, queryKey]);

  const startMutation = useMutation({
    mutationFn: ({ viccCode }: { viccCode: string }) =>
      apiStartQuoteViccLookup(quoteId, viccCode),
  });

  const triggerLookup = useCallback(
    async (viccCode: string): Promise<ViccLookupResult | null> => {
      if (!viccCode || viccCode.length === 0) return null;

      const cachedResult = queryClient.getQueryData<ViccLookupResult>([
        ...queryKey,
        viccCode,
      ]);
      if (cachedResult && isTerminalStatus(cachedResult)) {
        onResultRef.current?.(cachedResult);
        return cachedResult;
      }

      try {
        const result = await startMutation.mutateAsync({ viccCode });

        if (result.status === 'pending') {
          setPendingVicc(viccCode);
        } else {
          queryClient.setQueryData([...queryKey, viccCode], result);
          onResultRef.current?.(result);
        }

        return result;
      } catch {
        const errorResult: ViccLookupResult = {
          vicc_code: viccCode,
          status: 'error',
          error: 'VICC lookup failed. Please try again.',
        };
        onResultRef.current?.(errorResult);
        return errorResult;
      }
    },
    [queryClient, queryKey, startMutation],
  );

  const clear = useCallback(() => {
    setPendingVicc(null);
  }, []);

  const getStatus = (): ViccLookupStatus => {
    if (startMutation.isPending) return 'loading';
    if (pendingVicc) return 'polling';
    if (pollResult?.found || pollResult?.status === 'found') return 'found';
    if (pollResult?.status === 'error') return 'error';
    if (pollResult?.status === 'not_found') return 'not_found';
    return 'idle';
  };

  const status = getStatus();

  return {
    result: pollResult ?? null,
    status,
    isLoading: startMutation.isPending,
    isPolling: !!pendingVicc,
    triggerLookup,
    clear,
  };
};
